import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLocalContext } from '../../context/LocalContext';
import './Header.css';

const Header = () => {
  const { 
    isMenuOpen, 
    toggleMenu, 
    closeMenu, 
    isAuthenticated, 
    user, 
    logout,
    openLoginModal,
    openSignupModal
  } = useLocalContext();
  
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  /* Solid bar on inner pages (white bg); on home only when scrolled */
  const useSolidHeader = !isHomePage || isScrolled;

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sync scroll state + close menu on route change (fixes wrong colors after navigate)
  useEffect(() => {
    setIsScrolled(window.scrollY > 50);
    closeMenu();
  }, [location.pathname]);

  // Hamburger Icon
  const HamburgerIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {isMenuOpen ? (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      ) : (
        <>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      )}
    </svg>
  );

  // User Avatar
  const UserAvatar = () => (
    <div className="user-avatar">
      {user?.avatar ? (
        <img src={user.avatar} alt={user.name} />
      ) : (
        <span>{user?.name?.charAt(0) || 'U'}</span>
      )}
    </div>
  );

  return (
    <header className={`header ${useSolidHeader ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="header-logo" onClick={closeMenu}>
          <span className="tinder-logo" aria-label="DateHalo Logo">DateHalo</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="header-nav desktop-nav">
          <Link to="/find-love" className="nav-link">Find Love</Link>
          <Link to="/about" className="nav-link">Success Stories</Link>
          <Link to="/expert-help" className="nav-link">Safety Hub</Link>
        </nav>

        {/* Desktop Auth Buttons */}
        <div className="header-auth desktop-auth">
          {isAuthenticated ? (
            <div className="user-menu">
              <UserAvatar />
              <span className="user-name">{user?.name}</span>
              <button className="btn btn-outline btn-sm" onClick={logout}>
                Log out
              </button>
            </div>
          ) : (
            <>
              <button className="btn btn-text" onClick={openLoginModal}>
                Log in
              </button>
              <button className="btn btn-primary" onClick={openSignupModal}>
                Sign up
              </button>
            </>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button className="menu-toggle" onClick={toggleMenu} aria-label="Toggle menu">
          <HamburgerIcon />
        </button>

        {/* Mobile Navigation */}
        <div className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}>
          <nav className="mobile-nav-links">
            <Link to="/find-love" className="nav-link" onClick={closeMenu}>Find Love</Link>
            <Link to="/about" className="nav-link" onClick={closeMenu}>Success Stories</Link>
            <Link to="/expert-help" className="nav-link" onClick={closeMenu}>Safety Hub</Link>
            <Link to="/privacy" className="nav-link" onClick={closeMenu}>Privacy Policy</Link>
            <Link to="/terms" className="nav-link" onClick={closeMenu}>Terms of Service</Link>
          </nav>
          
          <div className="mobile-auth">
            {isAuthenticated ? (
              <div className="mobile-user-info">
                <UserAvatar />
                <span className="user-name">{user?.name}</span>
                <button className="btn btn-outline btn-block" onClick={logout}>
                  Log out
                </button>
              </div>
            ) : (
              <>
                <button className="btn btn-outline btn-block" onClick={openLoginModal}>
                  Log in
                </button>
                <button className="btn btn-primary btn-block" onClick={openSignupModal}>
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && <div className="mobile-overlay" onClick={closeMenu} />}
      </div>
    </header>
  );
};

export default Header;
